import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import Actions from 'Actions';
import type { StorytellerState } from 'store/StorytellerReduxState';
import { type FilterMetadataCollection, type FilterMetadata } from 'common/types/filterMetadata';

export interface GridFilterState {
  filterWhereClauses: Record<string, string | null>; // componentUuid -> filterWhereClause
  filterMetadata: FilterMetadataCollection; // componentUuid -> filter metadata
}

// Initial state
const initialState: GridFilterState = {
  filterWhereClauses: {},
  filterMetadata: {},
};

// Action creators
export const setFilterWhereClause = createAction(
  Actions.GRID_FILTER_WHERE_CLAUSE_SET,
  (componentUuid: string, filterWhereClause: string | null) => ({
    payload: { componentUuid, filterWhereClause },
  }),
);

export const setFilterMetadata = createAction(
  'GRID_FILTER_METADATA_SET',
  (componentUuid: string, filterMetadata: FilterMetadata[]) => ({
    payload: { componentUuid, filterMetadata },
  }),
);

// Reducer
export const gridFilterReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(setFilterWhereClause, (state, action) => {
      const { componentUuid, filterWhereClause } = action.payload;
      state.filterWhereClauses[componentUuid] = filterWhereClause;
    })
    .addCase(setFilterMetadata, (state, action) => {
      const { componentUuid, filterMetadata } = action.payload;
      state.filterMetadata[componentUuid] = filterMetadata;
    });
});

// Selectors
export const selectGridFilterSlice = (state: StorytellerState) => state.gridFilter;

export const getAllFilterWhereClauses = createSelector(
  selectGridFilterSlice,
  (slice) => slice.filterWhereClauses,
);

export const getAllFilterMetadata = createSelector(selectGridFilterSlice, (slice) => slice.filterMetadata);

export const selectFilterWhereClause = (componentUuid: string) =>
  createSelector(selectGridFilterSlice, (slice) => slice.filterWhereClauses[componentUuid] || null);

export const selectFilterMetadata = (componentUuid: string) =>
  createSelector(selectGridFilterSlice, (slice) => slice.filterMetadata[componentUuid] || []);
